import React from 'react';
import { useTranslation } from 'react-i18next';
import { getLocalDateKey } from './dateUtils';
import { getFlexiblePrecision, isTimelineAllDayTask } from './scheduleUtils';
import { TimelineDayOverlay } from './TimelineDayOverlay';
import { TimelineNonHourRow } from './TimelineNonHourRow';
import { useTimelineView } from './TimelineViewContext';
import { getPeriodLabel } from './timelineViewUtils';
import { useTimelineRows } from './useTimelineRows';

export const TimelinePeriod: React.FC<{ offset: number; periodDate: Date }> = ({ offset, periodDate }) => {
    const { i18n } = useTranslation();
    const {
        activeTimelineDateKey, getTasksForDay, onOpenDayInDaysView, timelineScale,
    } = useTimelineView();
    const rows = useTimelineRows()(periodDate);
    const allDayTasks = timelineScale === 'day'
        ? getTasksForDay(periodDate).filter(({ task }) => getFlexiblePrecision(task) === 'day' || isTimelineAllDayTask(task)) : [];
    const dateKey = getLocalDateKey(periodDate);
    const periodKey = timelineScale === 'years'
        ? `${timelineScale}-${periodDate.getFullYear() - 5}` : `${timelineScale}-${dateKey}`;
    return (
        <section className={`timeline-period timeline-period-${timelineScale} ${timelineScale === 'day' ? 'timeline-period-hours' : ''}`}
            data-timeline-current-period={offset === 0 ? 'true' : undefined}
            data-timeline-period-key={periodKey}
            data-timeline-date={timelineScale === 'day' ? dateKey : undefined}
            data-timeline-label={getPeriodLabel(periodDate, timelineScale, i18n.language)}>
            {timelineScale !== 'day' && (
                <div className={`timeline-period-header ${offset === 0 ? 'timeline-period-header-initial' : ''}`}>
                    <span>{getPeriodLabel(periodDate, timelineScale, i18n.language)}</span>
                </div>
            )}
            <div className={`timeline-rows timeline-rows-${timelineScale}`}>
                {timelineScale === 'day' && <TimelineDayOverlay periodDate={periodDate} />}
                {timelineScale !== 'day' ? rows.map((row) => <TimelineNonHourRow key={row.key} row={row} />)
                    : rows.map((row) => (
                        <div key={row.key} className={`timeline-slot ${row.isCurrent ? 'current' : ''}`}
                            onPointerDown={row.onPointerDown} data-timeline-date={dateKey}
                            data-timeline-hour={row.hour?.toString().padStart(2, '0')}
                            data-timeline-row-date={row.date ? getLocalDateKey(row.date) : undefined}>
                            <div className="timeline-label">
                                {row.hour === 0 && activeTimelineDateKey !== dateKey ? (
                                    <button type="button" className="timeline-midnight-date"
                                        onPointerDown={(event) => event.stopPropagation()}
                                        onClick={(event) => { event.stopPropagation(); onOpenDayInDaysView(periodDate); }}>
                                        {allDayTasks.length > 0 && <span className="timeline-all-day-dot" aria-hidden="true" />}
                                        <span>
                                            <strong>{periodDate.toLocaleDateString(i18n.language, { weekday: 'short' })}</strong>
                                            <small>{periodDate.toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' })}</small>
                                        </span>
                                    </button>
                                ) : row.hour === 0 ? null : row.label}
                            </div>
                            <div className="timeline-task-area" />
                        </div>
                    ))}
            </div>
        </section>
    );
};
